<template>
    <div>
        <!-- Bouton d'ajout de critère (si aucun existant) -->
		<b-row v-if="criterias.length == 0" class="p-1">
			<b-col cols="auto">
				<!-- $t('AddCriteria') / Ajout d'un critère -->
				<b-button v-if="SelectType" @click="newCriteria()" :variant="'outline-' + Variant" v-b-tooltip.noninteractive.hover :title="$t('AddCriteria')" class="btn-circle ml-3"><b-icon icon="plus"/></b-button>
			</b-col>
		</b-row>

		<!-- Parcours des critères -->
		<b-row v-for="(criteria, index) in criterias" :key="'criteria_' + index" class="p-1" align-v="center" :style="generateVariantStyle(Variant)">
			<!-- Bouton d'ajout de critère (cas existant) -->
			<b-col v-if="index == 0" cols="auto" >
				<b-row align-v="center">
					<b-col>
						<b-button v-if="SelectType" @click="newCriteria()" :variant="'outline-' + Variant" v-b-tooltip.noninteractive.hover :title="$t('AddCriteria')" class="btn-circle ml-3"><b-icon icon="plus"/></b-button>
					</b-col>
				</b-row>
			</b-col>

			<!-- Opérateur logique entre les critères -->
			<b-col v-else cols="1">
				<b-row align-v="center">
					<b-col>
						<b-form-select size="sm" v-if="index > 0" :options="Logicals" v-model="criteria.Operande" value-field="Name" text-field="Label" />
					</b-col>
				</b-row>
			</b-col>

			<!-- Suppression d'un critère -->
			<b-col cols="auto" :class="{'pl-0' : criteria.Operande != '' }">
				<b-row align-v="center">
					<b-col>
						<b-button @click="removeCriteria(criterias, index)" :variant="'outline-' + Variant" v-b-tooltip.noninteractive.hover :title="$t('DeleteCriteria')" class="btn-circle"><b-icon icon="dash"/></b-button>
					</b-col>
				</b-row>
			</b-col>

			<!-- Sélection du type de critère -->
			<b-col cols="4">
				<b-row align-v="center">
					<b-col>
						<label>{{ $t('Criteria') }}</label>

						<multiselect :id="$id('Criteria')"
						:options="getProperties(SelectType)"
						:disabled="false"
						:required="false"
						:close-on-select="true"
						:allow-empty="false"
						:searchable="true"
						:show-labels="false"
						:multiple="false"
						:max-height="150"
						track-by="Name"
						label="Label"
						:placeholder="$t('SelectCriteria')"
						class="mt-0 mb-0 ml-0 mr-3 pt-0 d-inline-block h-100"
						style="background: white !important; background-color: white !important"
						:value="getProperties(SelectType).find(c => c.Name === criteria.Name)"
						@input="(selectedOption) => selectProperty(selectedOption, criteria)">
							<template #caret={toggle}>
								<span class="multiselect__select pr-0">
									<b-icon icon="chevron-down" font-scale="0.55" shift-h="0" style="stroke: black !important; stroke-width: 1.5px !important;"/>
								</span>
							</template>

							<template #noResult>
								{{ $t('NoMatchingCriteria') }}
							</template>
						</multiselect>
					</b-col>
				</b-row>
			</b-col>

			<!-- Sélection de l'opérateur (afficher que si un critère est sélectionné) -->
			<b-col v-if="criteria.Name != ''" cols="3">
				<b-row align-v="center">
					<b-col>
						<label>{{ $t('Operator') }}</label>
					
						<multiselect :id="$id('Operator')"
						:options="getOperators(criteria.Type)"
						:disabled="false"
						:required="false"
						:close-on-select="true"
						:allow-empty="false"
						:searchable="true"
						:show-labels="false"
						:multiple="false"
						:max-height="150"
						track-by="Name"
						label="Label"
						:placeholder="$t('SelectOperator')"
						class="mt-0 mb-0 ml-0 mr-3 pt-0 d-inline-block h-100"
						style="background: white !important; background-color: white !important"
						:value="getOperators(criteria.Type).find(c => c.Name === criteria.Operator)"
						@input="(selectedOption) => selectOperator(selectedOption, criteria)"> 
							<template #caret={toggle}>
								<span class="multiselect__select pr-0">
									<b-icon icon="chevron-down" font-scale="0.55" shift-h="0" style="stroke: black !important; stroke-width: 1.5px !important;"/>
								</span>
							</template>

							<template #noResult>
								{{ $t('NoMatchingOperator') }}
							</template>
						</multiselect>
					</b-col>
				</b-row>
			</b-col>

			<!-- Sélection de la valeur (afficher que si un opérateur est sélectionné) -->
			<b-col v-if="criteria.Operator != ''" cols="3">
				<b-row align-v="center">
					<b-col>
						<label>{{ $t("Value") }}</label>
					
						<!-- Chaine de caractère -->
						<b-form-input v-if="criteria.Type === 'String'" v-model="criteria.Value" :placeholder="$tc('SelectValue', 1)" />

						<!-- Chiffre -->
						<b-form-input v-else-if="['Int','Long','Mark','Factor','Float'].some(type => criteria.Type.includes(type))" type="number" v-model="criteria.Value" :placeholder="$tc('SelectValue', 1)" />

						<!-- Sélection Oui / Non / Aucun -->
						<b-form-radio-group v-else-if="['YesNoEnum', 'Bool'].includes(criteria.Type)" :options="yesNoList.filter(x => x.Types.includes(criteria.Type))" text-field="Text" value-field="Value" v-model="criteria.Value" class="pt-1" plain />

						<!-- Enumeration -->
						<multiselect v-else-if="criteria.Type.includes('EnumerationEnum') || ['Tariff', 'Contract', 'Provider'].some(type => criteria.Type.includes(type))" :id="$id('EnumerationEnum')"
						:options="listItems(criteria.Type.replace('EnumerationEnum.', ''))"
						:disabled="false"
						:required="false"
						:close-on-select="true"
						:allow-empty="false"
						:searchable="true"
						:show-labels="false"
						:multiple="false"
						:max-height="150"
						track-by="Id"
						label="Label"
						:placeholder="$tc('SelectValue', 1)"
						class="mt-0 mb-0 ml-0 mr-3 pt-0 d-inline-block h-100"						
						:value="listItems(criteria.Type.replace('EnumerationEnum.', '')).find(c => c.Id === criteria.Value)"
						@input="(selectedOption) => selectValue(selectedOption, criteria)">
						<template #caret={toggle}>
								<span class="multiselect__select pr-0">
									<b-icon icon="chevron-down" font-scale="0.55" shift-h="0" style="stroke: black !important; stroke-width: 1.5px !important;"/>
								</span>
							</template>

							<template #noResult>
								{{ $t('NoMatchingValue') }}
							</template>
						</multiselect>

						<!-- MultiEnumeration -->
						<multiselect v-else-if="criteria.Type.includes('MultiEnumeration')" :id="$id('MultiEnumeration')"						
						:options="listItems(criteria.Type.replace('MultiEnumeration.', ''))"
						:disabled="false"
						:required="false"
						:close-on-select="false"
						:allow-empty="true"
						:searchable="true"
						:show-labels="false"
						:multiple="true"
						:max-height="150"
						track-by="Id"
						label="Label"
						:placeholder="$tc('SelectValue', 2)"
						class="mt-0 mb-0 ml-0 mr-3 pt-0 d-inline-block h-100"
						v-model="criteria.Value"										
						> <!-- :value="listItems(criteria.Type.replace('MultiEnumeration.', '')).find(c => c.Id === criteria.Value)" -->
						<template #caret={toggle}>
								<span class="multiselect__select pr-0">
									<b-icon icon="chevron-down" font-scale="0.55" shift-h="0" style="stroke: black !important; stroke-width: 1.5px !important;"/>
								</span>
							</template>

							<template #noResult>
								{{ $t('NoMatchingValue') }}
							</template>
						</multiselect>

						<!-- Correcteur météo -->
						<multiselect v-else-if="criteria.Type.includes('MeteoCorrector')" :id="$id('MeteoCorrector')"
						:options="listItems(criteria.Name)"
						:disabled="false"
						:required="false"
						:close-on-select="true"
						:allow-empty="false"
						:searchable="true"
						:show-labels="false"
						:multiple="false"
						:max-height="150"
						track-by="Id"
						label="Label"
						:placeholder="$tc('SelectValue', 1)"
						class="mt-0 mb-0 ml-0 mr-3 pt-0 d-inline-block h-100"
						style="background: white !important; background-color: white !important"
						:value="listItems(criteria.Name).find(c => c.Id === criteria.Value)"
						@input="(selectedOption) => selectValue(selectedOption, criteria)">
						<template #caret={toggle}>
								<span class="multiselect__select pr-0">
									<b-icon icon="chevron-down" font-scale="0.55" shift-h="0" style="stroke: black !important; stroke-width: 1.5px !important;"/>
								</span>
							</template>

							<template #noResult>
								{{ $t('NoMatchingValue') }}
							</template>
						</multiselect>

						<!-- Date -->
						<b-form-input v-else-if="criteria.Type.includes('Date')" type="date" v-model="criteria.Value" />
					</b-col>
				</b-row>
			</b-col>
		</b-row>
    </div>
</template>

<script lang="ts">
import {Component, Prop, PropSync, Vue} from 'vue-property-decorator';
import { Getter } from 'vuex-class';
import Dictionary from '@/types/Dictionary';
import ListItem from '@/types/fields/ListItem';
import Criteria from '@/types/FilterTree/Criteria';
import Property from '@/types/Property';
import Multiselect from 'vue-multiselect';
import { mixins } from 'vue-class-component';
import { ThemeUtils } from '../Utils';
import locales from "./locales/CriteriasPropertyFilter.json";

/**
 * Composant permettant la gestion des critères pour un filtre par propriétés
 */
@Component({
	components: {
		Multiselect
	},
	i18n: { messages: locales }
})
export default class CriteriasPropertyFilter extends mixins(ThemeUtils) {
    /** Getter afin de récupérer une liste selon un nom spécifique */
    @Getter('GetListItems')
	private listItems!: (listName: string) => ListItem[];

    /** Variant de couleur */
    @Prop()
    public Variant !: string;

    /** Liste des critères du filtre */
    @PropSync("Criterias", { default : () => [] })
    public criterias !: Criteria[];

    /** Liste des propriétés Complètes */
    @Prop()
    public Properties !: Dictionary<Property[]>;

    /** Type d'objet du filtre sélectionné */
    @Prop()
    public SelectType !: string;

    /** Liste des opérateurs logiques (ET /OU ) */
    @Prop()
	private Logicals !: { Name: string, Label: string }[];

    /** @Liste YesNoEnum à charger dynamiquement !!! */
	private yesNoList : {Value : string, Text: string, Types : string[]}[] = 
    [
        { Value: 'Yes', Text: this.$t('Yes').toString(), Types: ["YesNoEnum", "Bool"] }, // $t('Yes') / Oui
        { Value: 'No', Text: this.$t('No').toString(), Types: ["YesNoEnum", "Bool"] }, // $t('No') / Non
		{ Value: 'None', Text: this.$t('None').toString(), Types: ["YesNoEnum"] }
    ];

    /** Liste des opérateurs liant une propriéte et sa valeur */
	private operators: { Name: string, Label: string, Types: string[] }[] = 
    [
		{ Name: 'Inf', Label: this.$t('LessThan').toString(), Types: ["Int", "Mark", "Factor", "Float", "Date", ] }, //$t('LessThan') / Moins que
		{ Name: 'Sup', Label: this.$t('GreaterThen').toString(), Types: ["Int", "Mark", "Factor", "Float", "Date"] }, //$t('GreaterThen') / Plus que
		{ Name: 'Not', Label: this.$t('DifferentFrom').toString(), Types: ["Provider", "Contract", "Tariff", "Int", "Mark", "Factor", "Float", "String", "EnumerationEnum", "MultiEnumeration", "Date", "MeteoCorrector", "YesNoEnum", "Bool"] }, // $t('DifferentFrom') / Différent de 
		{ Name: 'Equal', Label: this.$t('EqualTo').toString(), Types: ["Provider", "Contract", "Tariff", "Int", "Mark", "Factor", "Float", "String", "EnumerationEnum", "MultiEnumeration", "Date", "MeteoCorrector", "YesNoEnum", "Bool"] }, // $t('EqualTo') / Egual à
		{ Name: 'InfEqual', Label: this.$t('LessThanOrEqualTo').toString(), Types: ["Int", "Mark", "Factor", "Float", "Date"] }, // $t('LessThanOrEqualTo') / Moins que ou égual à
		{ Name: 'SupEqual', Label: this.$t('GreaterThanOrEqualTo').toString(), Types: ["Int", "Mark", "Factor", "Float", "Date"] }, // $t('GreaterThanOrEqualTo') / Plus grand que ou égual à
		{ Name: 'Contains', Label: this.$t('Contains').toString(), Types: ["String", "MeteoCorrector", "MultiEnumeration"] }, // $t('Contains') / Contient
		{ Name: 'NotContains', Label: this.$t('NotContains').toString(), Types: ["String", "MeteoCorrector", "MultiEnumeration"] } // $t('Contains') / Contient
    ];

    /**
	* Permet d'obtenir la liste des opérateurs disponibles pour un type donnée
	* @param type Type de valeur
	*/
    private getOperators(type: string): { Name: string, Label: string }[] {
		let result: { Name: string, Label: string }[] = [];
		for (let operator of this.operators) {
			if (operator.Types.findIndex(t => type.includes(t)) > -1) {
				result.push(operator);
			}
		}
		return result;
	}

	/**
	 * Permet de retourner la liste des propriétés
	 * @param selectType Type d'objet sélectionné
	 */
	private getProperties(selectType : string) : Property[]{
		return this.Properties[selectType];
	}

    /**
    * Permet la sélection des élements d'une propriété pour alimenter le critère
	* @param selectedOption Sélection
    * @param selectedType Type d'objet sélectionné
	* @param criteria Critère à alimenter
	*/
	private async selectProperty(selectedOption : Property, criteria: Criteria) {
		console.debug("CriteriasPropertyFilter - selectProperty - selectedOption : %O - selectedType : %O - criteria : %O", selectedOption, this.SelectType, criteria);

		// let p: Property = this.Properties[this.SelectType].find(x => x.Name == selectedOption.Name);

		//console.debug("CriteriasPropertyFilter - selectProperty - p : %O", p);

        if (selectedOption) {			
			Vue.set(criteria, "Name", selectedOption.Name);
			Vue.set(criteria, "Type", selectedOption.Type);
			Vue.set(criteria, "Value", ['YesNoEnum', 'Bool'].includes(selectedOption.Type) ? this.yesNoList.at(0).Value : "");
			if (criteria.Type.includes("MultiEnumeration")) {
				let listName = criteria.Type.replace("MultiEnumeration.", "");
				await this.$store.dispatch("LoadListItems", [{ listName: listName }]);
			}

			if (criteria.Type.includes("EnumerationEnum") || ["Tariff", "Contract", "Provider"].some(type => criteria.Type.includes(type))) {
				let listName = criteria.Type.replace("EnumerationEnum.", "");
				await this.$store.dispatch("LoadListItems", [{ listName: listName }]);
			}

			if(criteria.Type.includes("MeteoCorrector")){
				await this.$store.dispatch("LoadListItems", [{ listName: criteria.Name }])
			}
		}
	}

	/**
	 * Permet de sélectionner l'opérateur
	 * @param selectedOption 
	 * @param criteria 
	 */
	private selectOperator(selectedOption : { Name: string, Label: string, Types: string[] }, criteria: Criteria) {
		console.debug("CriteriasPropertyFilter - selectOperator - selectedOption : %O - criteria : %O", selectedOption, criteria);

		Vue.set(criteria, "Operator", selectedOption.Name);
	}

	/**
	 * Permet de sélectionner la valeur provenant d'un liste déroulante
	 * @param selectedOption 
	 * @param criteria 
	 */
	private selectValue(selectedOption : ListItem, criteria : Criteria) {
		console.debug("CriteriasPropertyFilter - selectValue - selectedOption : %O - criteria : %O", selectedOption, criteria);

		Vue.set(criteria,"Value", selectedOption.Id);
	}

    /**
     * Ajout d'un nouveau critère vierge
     */
	private newCriteria() {
		console.debug("CriteriasPropertyFilter - newCriteria - criterias", this.criterias);

		if(this.criterias.length > 0)
			this.criterias.push({ Operande: "And", Name: "", Type: "", Operator: "", Value: "" });
		else
			this.criterias.push({ Operande: "", Name: "", Type: "", Operator: "", Value: "" });
	}

    /**
	* Suppression d'un critère à une position donnée
    * @param criterias Liste des critères à modifier
	* @param index position du critère à supprimer
	*/
	private removeCriteria(criterias: Criteria[], index: number) {
		criterias.splice(index, 1);

		// Si le premier critère contient un opérande, on change sa valeur à vide
		if(criterias.length > 0 && criterias.at(0).Operande != "")
			criterias.at(0).Operande = "";
	}
}
</script>

<style scoped>
:deep(input[type=radio]:checked) {
        /** Change la couleur du bouton radio */
        accent-color: var(--Variant) !important;
    }
</style>